"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Droplets,
  Zap,
  Tractor,
  Cog,
  Factory,
} from "lucide-react";
import { PALETTE } from "../lib/colorPallete";
import { ICarouselItem } from "../types/carousel.types";
import axios from "axios";

const CARD_WIDTH_DESKTOP = "w-[260px]";
const CARD_WIDTH_TABLET = "w-[220px]";
const CARD_WIDTH_MOBILE = "w-[calc(33.333%-0.34rem)]";
const SLIDE_DURATION = 3500;

const iconMap = {
  droplets: Droplets,
  zap: Zap,
  tractor: Tractor,
  factory: Factory,
  cog: Cog,
};

type CarouselItemWithIcon = ICarouselItem & {
  icon: React.ReactElement;
};

const CarouselCard = ({ item }: { item: CarouselItemWithIcon }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { margin: "-20% 0px -20% 0px" });

  useEffect(() => {
    if (!isInView || item.imageUrls.length <= 1) return;

    const timer = setInterval(() => {
      setImgIndex((prev) => (prev + 1) % item.imageUrls.length);
    }, SLIDE_DURATION);

    return () => clearInterval(timer);
  }, [isInView, item.imageUrls.length]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative ${CARD_WIDTH_MOBILE} sm:${CARD_WIDTH_TABLET} md:${CARD_WIDTH_DESKTOP} 
        aspect-square h-auto sm:aspect-auto sm:h-[250px] md:h-[280px] 
        bg-stone-950 rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 hover:shadow-2xl hover:-translate-y-1 group cursor-pointer transition-all duration-500`}
    >
      <Link href={item.link} className="block h-full w-full">
        { }
        <div className="absolute top-0 left-0 w-full z-20 p-1.5 sm:p-3 flex justify-between items-start pointer-events-none">
          <div className="p-1 sm:p-1.5 rounded-lg sm:rounded-xl bg-black/50 backdrop-blur-lg border border-white/10 text-white/90 shadow-lg">
            {/* eslint-disable-next-line */}
            {React.cloneElement(item.icon as React.ReactElement<any>, {
              size:
                typeof window !== "undefined" && window.innerWidth < 640
                  ? 12
                  : 20,
            })}
          </div>
          <div className="font-mono text-[6px] sm:text-[8px] md:text-[9px] text-amber-400 tracking-widest px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-black/50 border border-amber-500/20 uppercase shadow-lg backdrop-blur-md">
            ID: ...{item._id.slice(-4)}
          </div>
        </div>

        { }
        <div
          className="absolute inset-0 z-0"
          style={{ backgroundColor: PALETTE.deepGreen }}
        >
          <AnimatePresence mode="popLayout">
            <motion.div
              key={`${item._id}-${imgIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              className="absolute inset-0"
            >
              <Image
                src={item.imageUrls[imgIndex]}
                alt={item.title}
                fill
                className="object-cover"
                style={{
                  filter: "grayscale(10%) contrast(1.1) brightness(0.9)",
                }}
              />
            </motion.div>
          </AnimatePresence>

          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 mix-blend-overlay bg-amber-500/20 pointer-events-none" />

          <div className="absolute inset-0 bg-linear-to-t from-stone-950 via-stone-950/40 to-transparent opacity-90" />

          <div className="absolute inset-0 opacity-20 pointer-events-none" />
        </div>

        { }
        <div className="absolute bottom-0 left-0 w-full z-20 p-2 sm:p-4 flex flex-col justify-end">
          { }
          {item.imageUrls.length > 1 && isInView && (
            <div className="flex gap-0.5 sm:gap-1 mb-2 sm:mb-3 opacity-30 group-hover:opacity-100 transition-opacity">
              {item.imageUrls.map((_, i) => (
                <div
                  key={i}
                  className="h-[2px] flex-1 bg-white/20 rounded-full overflow-hidden"
                >
                  {i === imgIndex && (
                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{
                        duration: SLIDE_DURATION / 1000,
                        ease: "linear",
                      }}
                      className="h-full bg-amber-400"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="transform transition-transform duration-500 group-hover:-translate-y-1 sm:group-hover:-translate-y-2">
            { }
            <span className="text-amber-400 text-[7px] sm:text-[10px] md:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase block mb-0.5 sm:mb-1 opacity-90 group-hover:opacity-100 transition-opacity whitespace-nowrap overflow-hidden text-ellipsis">
              {item.subtitle}
            </span>

            { }
            <h3 className="font-serif text-[11px] xs:text-[13px] sm:text-lg md:text-xl text-white mb-1 sm:mb-2 leading-tight drop-shadow-md overflow-hidden text-ellipsis">
              {item.title}
            </h3>

            { }
            <div className="h-px w-full bg-white/10 my-1 sm:my-2.5 md:my-3 group-hover:bg-amber-500/50 transition-colors" />

            { }
            <div className="flex items-center justify-between">
              <span className="text-stone-400 text-[8px] xs:text-[9px] sm:text-sm font-medium">
                {item.stats}
              </span>
              <div className="flex items-center gap-0.5 sm:gap-2 text-white/80 text-[7px] xs:text-[8px] sm:text-xs md:text-sm font-bold uppercase tracking-wider group-hover:text-amber-400 transition-colors">
                View{" "}
                <ArrowRight
                  size={12}
                  className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4"
                />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default function CategoryCarousel() {
  const [categories, setCategories] = useState<CarouselItemWithIcon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCarousel = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get("/api/carousel");
        const items: ICarouselItem[] = response.data;

        const itemsWithIcons = items.map((item) => ({
          ...item,
          icon: React.createElement(iconMap[item.iconName] || iconMap.cog),
        }));

        setCategories(itemsWithIcons);
      } catch (err) {
        console.error("Error fetching carousel:", err);
        setError("Failed to load categories");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCarousel();
  }, []);

  return (
    <section
      className="relative py-8 sm:py-10 md:py-12"
      style={{ backgroundColor: PALETTE.darkGreen }}
    >
      { }
      <div className="container mx-auto px-4 sm:px-6 md:px-8 mb-6 sm:mb-7 md:mb-8 flex items-end justify-between">
        <div className="text-center md:text-left w-full">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white mb-1 sm:mb-2">
            Our Range
          </h2>
          <p className="text-white/60 text-sm sm:text-base max-w-sm mx-auto md:mx-0">
            Professional agricultural equipment engineered for performance.
          </p>
        </div>
      </div>

      { }
      <div
        className="flex flex-wrap justify-start gap-2 sm:gap-4 md:gap-6 px-2 sm:px-6 md:px-8 pb-8 sm:pb-9 md:pb-10"
      >
        {isLoading ? (
          <div className="text-white text-center py-20">
            Loading categories...
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-20">{error}</div>
        ) : categories.length === 0 ? (
          <div className="text-white/60 text-center py-20">
            No categories available
          </div>
        ) : (
          <>
            {categories.map((item: CarouselItemWithIcon) => (
              <CarouselCard key={item._id} item={item} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}
